import React, { useState, useEffect } from 'react';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import { supabase } from '../supabaseClient';

export default function ChangeABelief({ goBack, goHome, navigateTo }) {
    const [showRestrictionModal, setShowRestrictionModal] = useState(null);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setShowRestrictionModal({ message: 'Please log in to start a session.', redirect: 'login' });
                return;
            }

            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('membership_type, session_complete, session_start_date, created_at, next_membership_start')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'login' });
                return;
            }

            console.log('Debug: Profile data:', {
                membership_type: profile.membership_type,
                session_complete: profile.session_complete,
                session_start_date: profile.session_start_date,
                created_at: profile.created_at,
                next_membership_start: profile.next_membership_start
            });

            if (profile.membership_type === 'Free Subscription') {
                const now = new Date();
                console.log('Debug: Current UTC time:', now.toISOString());

                // 24-hour timeout check for incomplete sessions
                if (profile.session_start_date && !profile.session_complete) {
                    const sessionStart = new Date(profile.session_start_date + 'Z');
                    const hoursSinceStart = (now - sessionStart) / (1000 * 60 * 60);
                    console.log('Debug: Session timeout check:', {
                        session_start_date: profile.session_start_date,
                        parsed_session_start: sessionStart.toISOString(),
                        hoursSinceStart: hoursSinceStart.toFixed(2),
                        is_timeout: hoursSinceStart > 24
                    });
                    if (hoursSinceStart > 24) {
                        const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                            ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                            : new Date(profile.created_at);
                        const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                        const monthsSinceBase = Math.floor(daysSinceBase / 30);
                        const resetDate = new Date(baseDate);
                        resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);
                        setShowRestrictionModal({
                            message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                            redirect: 'back'
                        });
                        return;
                    }
                }

                // Monthly session limit check
                const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                    ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                    : new Date(profile.created_at);
                const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                const monthsSinceBase = Math.floor(daysSinceBase / 30);
                const resetDate = new Date(baseDate);
                resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);

                if (profile.session_complete && daysSinceBase < (monthsSinceBase + 1) * 30) {
                    console.log('Debug: Monthly limit reached, reset date:', resetDate.toLocaleDateString());
                    setShowRestrictionModal({
                        message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                        redirect: 'back'
                    });
                    return;
                }

                // Update session_start_date for new sessions
                if (!profile.session_complete && !profile.session_start_date) {
                    try {
                        const { error: updateError } = await supabase
                            .schema('api')
                            .from('user_profiles')
                            .update({
                                session_start_date: new Date().toISOString(),
                            })
                            .eq('id', user.id);

                        if (updateError) {
                            console.error('Error updating session_start_date:', updateError.message);
                            setShowRestrictionModal({ message: 'Failed to start the session. Please try again.', redirect: 'back' });
                            return;
                        }

                        console.log('Session start date updated successfully.');
                    } catch (err) {
                        console.error('Unexpected error updating session_start_date:', err);
                        setShowRestrictionModal({ message: 'An unexpected error occurred while starting the session.', redirect: 'back' });
                        return;
                    }
                }
            }
        };

        checkSession();
    }, [navigateTo, goBack]);

    const handleSaveProgress = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in!');
                return;
            }

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert({
                    user_id: user.id,
                    current_question_id: null,
                    current_component: 'healingFormulasPage/changeABelief',
                    animated_helper: 'no', // always set to 'no'
                    timestamp: new Date().toISOString(),
                });

            if (progressError) {
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            alert('Session progress has been saved successfully!');
        } catch (err) {
            alert('An unexpected error occurred while saving progress.');
        }
    };

    // Render modal if restriction or error occurs
    if (showRestrictionModal) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    maxWidth: window.innerWidth <= 600 ? '90vw' : '400px',
                    width: '100%',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                    color: '#4B5563',
                }}>
                    <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{showRestrictionModal.message}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        {showRestrictionModal.redirect === 'back' && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking Back button');
                                    setShowRestrictionModal(null);
                                    goBack();
                                }}
                            >
                                Back
                            </button>
                        )}
                        {(showRestrictionModal.redirect === 'back' || showRestrictionModal.redirect === null) && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking My Account button, navigating to myAccount');
                                    setShowRestrictionModal(null);
                                    navigateTo('myAccount');
                                }}
                            >
                                My Account
                            </button>
                        )}
                        {showRestrictionModal.redirect && showRestrictionModal.redirect !== 'back' && showRestrictionModal.redirect !== null && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking OK button, navigating to', showRestrictionModal.redirect);
                                    setShowRestrictionModal(null);
                                    navigateTo(showRestrictionModal.redirect);
                                }}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const textStyle = {
        fontFamily: 'Times New Roman, Times, serif',
        fontSize: '16px',
        fontWeight: 'normal',
        color: 'black',
        textAlign: 'center',
        lineHeight: '1.5',
        marginBottom: '16px',
    };

    const listStyle = {
        fontFamily: 'Times New Roman, Times, serif',
        fontSize: '16px',
        fontWeight: 'normal',
        color: 'black',
        textAlign: 'center',
        lineHeight: '1.5',
        marginBottom: '8px',
    };

    const buttonStyle = {
        backgroundImage: `url(${buttonImage})`,
        backgroundSize: 'cover',
        border: '2px solid black',
        borderRadius: '10px',
        padding: '10px 20px',
        fontFamily: 'Times New Roman, Times, serif',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        width: '100%',
        maxWidth: '280px',
        textAlign: 'center',
    };

    return (
        <div
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px',
            }}
        >
            <h2
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '36px',
                    fontWeight: 'bold',
                    color: '#4B5563',
                    textAlign: 'center',
                    marginTop: '16px',
                }}
            >
                Change a Belief
            </h2>
            {/* Session Helper Cards Row */}
            <div
                className="changeabelief-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• Identify the root cause of your issue as a belief that limits you</div>
                        <div>• Identify what you would have to believe to allow what you prefer.</div>
                        <div>• Sync the hemispheres of your brain by crossing your left hand to your right knee and your right hand to your left knee repeatedly.</div>
                        <div>• Use Tapping to help move the old energy of belief out and sync the new belief in.</div>
                        <div>• Repetition is a valid way to change a belief it just takes longer.</div>
                        <div>• We are limited by what we believe about ourselves, our lives and about other people.</div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. What do I have to believe for this to be my reality? (based on the idea that we create our reality by what we believe)</div>
                        <div>2. Ask yourself repeatedly "what would be so bad about that?" Each time you answer, ask yourself again until you get to a fear at the base of your belief.</div>
                        <div>3. Ask yourself "What if...?" See what possibilities arise when you offer yourself the opportunity to explore.</div>
                        <div>4. What have I learned that I would not have otherwise learned by having this experience? (This can allow you to understand what you were trying to learn through the limiting experience of having the belief you may now choose to change)</div>
                        <div>5. Can you identify where your belief came from on this topic?</div>
                        <div>6. Reflect on a changed belief in past tense by asking "What did I start believing in order to get these desired results?" </div>
                    </div>
                </div>
            </div>
            {/* New Card below the two top cards, above the stacked column */}
            <div style={{
                width: '100%',
                maxWidth: '600px',
                border: '2px solid black',
                borderRadius: '10px',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                marginBottom: '20px',
                padding: '20px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <p style={textStyle}>
                    Repetition may be the way we notice an unwanted pattern in ourselves, but it can also be how we program ourselves with something preferred. <br /> Repetition is very important to the human operating system. We want to use this to our advantage without letting it get us stuck past the expiry date of a comfort zone we might find ourselves in eventually.
 Affirmations work on the premise of repetition. According to Dr. Bruce Lipton, the human brain is in the Theta wave stage up to age 7. This phase of the brain is also visited right before sleep or upon waking.
The Theta phase of brain waves allows the programming of our mind. Another way to program us is repetition. Many people combine these two methods to play their affirmations at bedtime or in the morning.
                </p>
            </div>
            {/* Main Cards */}
            <div style={{
                width: '100%',
                maxWidth: '600px',
                border: '2px solid black',
                borderRadius: '10px',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                marginBottom: '20px',
                padding: '20px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <p style={textStyle}>
                    Dr. Bruce Lipton offers a great at length explanation of why this is important. I encourage you to research this topic further if it interests you and find the methods that work for you.
                </p>
                <p style={textStyle}>
                    Energy Psychology offers instruction and guidance for changing the beliefs you have. The reason you might want to look at changing your belief is because we end up living out our beliefs. If we have a limiting belief, then that is the equivalent of a misunderstanding of truth. We will live out the consequences of that belief, which will eventually bring us to the correct understanding.
                </p>
                <p style={textStyle}>
                    Sometimes in our self-reflection work, we discover that we have been believing something that is unhelpful to our desires or goals. In this case, we want to be able to change this belief. I know that at least one method of changing a belief involves syncing both sides of the brain before repeating the new belief. There are also great techniques like Faster EFT, which is a tapping method that helps to shift the energy rapidly. I have found that different methods of this have worked well for me at different times.
                </p>
                <p style={textStyle}>
                    My understanding is that we are here to have our unique experiences with and through the energy that we have and even the energy that we misunderstand. For this reason, it is important that we do not beat ourselves up over having the beliefs we have. The beliefs we have are useful to us until they are not. When it is time to change a belief, it will come to your awareness. Sometimes this happens through a deep self-inquiry.
                </p>
            </div>
            <div style={{
                width: '100%',
                maxWidth: '600px',
                border: '2px solid black',
                borderRadius: '10px',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                marginBottom: '20px',
                padding: '20px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <p style={textStyle}>
                   If we live out whatever we believe it can make it undesirable to hold onto beliefs like, Rich people are greedy, We have to work hard to get by, Kids don't listen, I am not beautiful, I am always tired, life is not fair, the world is going to hell, People suck, etc.   It might not seem possible right now, but it is entirely possible to change those beliefs and start living the new results of your new beliefs.  
                </p>
                <p style={listStyle}>
                    The thing is we may have spent a lot of time practicing the old belief so there is some momentum built up in the direction of that old belief.  When we want to make changes we need to keep believing in the limitless possibilities before we even have any proof.  We have to flush the stream out with our new habits of thought before any of the stuff will materialize.  Also, take into consideration that our soul has an intention for being here on Earth and we do not want to introduce a contradictory belief to what our soul is trying to achieve.  It is best to let ourselves be open to the possibilities. Otherwise, changing a belief could have us just learning more about the unexpected results of our assumptions. 
                </p>
                <p style={listStyle}>
                   It is wise to let ourselves be lead by our hearts and aim to create the win, win, win.  If we were to believe we could control someone else, for instance, that would backfire as life would soon show us that we could be controlled.  It is best to have beliefs that align with our authentic being.  Those beliefs will be inspired by our soul and our journey up until now.
                </p>
                <p style={listStyle}>
                    The real gold in using this technique to effect change in our lives is that when we are shown that the reason for our suffering is rooted in a belief we hold, we can put the effort in to change that belief and give ourselves relief. 
                </p>
                <p style={listStyle}>
                    Faster EFT is a tapping method that allows us to reprogram our beliefs and if it interests you, please look into it further. <br />   Another great method of balancing the energy on a topic where you are open to changing your belief is Byron Katie's Judge your neighbor worksheet.  She offers it for free online.  She also has tutorial videos available for free on youtube.  The worksheet is very effective for helping us see a new perspective in any situation where we feel wronged, or like things are not fair. 
                </p>
            </div>
            <div style={{
                width: '100%',
                maxWidth: '600px',
                border: '2px solid black',
                borderRadius: '10px',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                boxShadow: '3px 3px 12px rgba(0,0,0,0.22), 0 1.5px 4px rgba(0,0,0,0.15)', // <-- add shadow to card
                marginBottom: '20px',
                padding: '20px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <button
                        style={{
                            ...buttonStyle,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.22), 0 1.5px 4px rgba(0,0,0,0.15)' // <-- add shadow to button
                        }}
                        onClick={() => {
                            console.log('Debug: Clicking Session Completion button, navigating to endofSession');
                            navigateTo('endofSession');
                        }}
                    >
                        Session Completion
                    </button>
                </div>
            </div>
            <div
                className="changeabelief-nav-btn-row"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '16px',
                    marginBottom: '16px',
                }}
            >
                <button
                    style={buttonStyle}
                    onClick={() => {
                        console.log('Debug: Clicking Back button');
                        goBack();
                    }}
                >
                    Back
                </button>
                <button
                    style={buttonStyle}
                    onClick={() => {
                        console.log('Debug: Clicking Home button');
                        goHome();
                    }}
                >
                    Home
                </button>
                <button
                    style={buttonStyle}
                    onClick={handleSaveProgress}
                >
                    Save
                </button>
            </div>
            <style>
                {`
                @media (max-width: 600px) {
                    .changeabelief-nav-btn-row {
                        position: static !important;
                        width: 100%;
                        background: none;
                        border-top: none;
                        border-radius: 0;
                        box-shadow: none;
                        padding: 0;
                        z-index: auto;
                        display: flex;
                        justify-content: center;
                        gap: 10px;
                        margin-top: 32px !important;
                        margin-bottom: 16px !important;
                    }
                    body {
                        padding-bottom: 0 !important;
                    }
                }
                `}
            </style>
        </div>
    );
}